import { FC } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import { Tiny2 } from '@/components/atoms/typography'
import styled, { css } from 'styled-components'
import { makeCalcCss } from '@/utils/cssHelpers'
import { ArrowDownIconDefault } from '../atoms/icons/ArrowDownIconDefault'
import { validFor } from '@/theme/validFor'
import { BadgeRedesign } from '../atoms/badge'
import { useFilterCountByType } from '@/providers/filterProvider/filtersProvider'
import { FilterQueryItem } from '@/types/filterQueryItem'

export enum DropdownItemWithArrowSide {
  right,
  left
}

type DropdownItemWithArrowProps = {
  title: string
  side?: DropdownItemWithArrowSide
  Icon?: (IconButtonProps) => JSX.Element
  highlight?: boolean
  hideText?: boolean
  dropdownKey: string
  isOpen: boolean
  onToggle: () => void
  filterType: FilterQueryItem
  parentId?: string
} & Rest

const DropdownItemWithArrowRedesign: FC<DropdownItemWithArrowProps> = ({
  title,
  children,
  Icon,
  side = DropdownItemWithArrowSide.left,
  highlight = false,
  hideText,
  isOpen,
  onToggle,
  filterType,
  parentId,
  ...rest
}) => {
  const count = useFilterCountByType(filterType, parentId)
  return (
    <Wrapper
      data-dropdown-wrapper
      highlight={highlight}
      {...rest}
      onClick={onToggle}
    >
      <StyledFlex cursorOnHover={true} zIndex={8}>
        {Icon && <Icon />}
        {count > 0 && <StyledBadgeRedesign counter={count} />}
        {highlight ? (
          <StyledTiny2 hideText={hideText}>{title}</StyledTiny2>
        ) : (
          <StyledTiny2Medium hideText={hideText}>{title}</StyledTiny2Medium>
        )}
        <RotableArrow
          hideText={hideText}
          open={isOpen}
          height={12}
          width={16}
        />
      </StyledFlex>
      {isOpen && (
        <DropdownContainer
          side={side}
          data-testid="dropdownContainer"
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          {children}
        </DropdownContainer>
      )}
    </Wrapper>
  )
}

export default DropdownItemWithArrowRedesign

const hideTextMediaQueries = css`
  @media screen and (min-width: 1200px) and (max-width: 1300px) {
    ${({ hideText }) => hideText && 'display: none;'}
  }
`

const RotableArrow = styled(ArrowDownIconDefault)<{ $open: boolean }>`
  transition: transform 0.2s ease-out;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  ${hideTextMediaQueries}
`

const makeDropdownContainerSideProperty = (
  side: DropdownItemWithArrowSide
): string => {
  const offset = '-1px'
  switch (side) {
    case DropdownItemWithArrowSide.left:
      return `left: ${offset}`
    case DropdownItemWithArrowSide.right:
      return `right: ${offset}`
  }
}

type DropdownContainerProps = {
  side: DropdownItemWithArrowSide
}

const kSidePadding = 0
const shadow = 'box-shadow: 0px 4px 4px 0px #75757533'

const StyledFlex = styled(Flex)`
  align-items: center;
  gap: 8px;
`

const DropdownContainer = styled<DropdownContainerProps>(Box)`
  padding: 0 ${kSidePadding}px;
  position: absolute;
  ${(props) => makeDropdownContainerSideProperty(props.side)};
  top: calc(100% + 8px);
  width: max-content;
  max-width: 500px;
  min-height: 50px;
  ${makeCalcCss('min-width', `100% - ${kSidePadding * 2}px`)};
  overflow-y: auto;
  overflow-x: visible;
  background-color: ${(props) => props.theme.colors.background};
  border: solid 1px ${(props) => props.theme.colors.buttonBorderNew};
  ${shadow};
  z-index: 10;
  border-radius: 4px;
`

const Wrapper = styled(Flex)`
  position: relative;
  align-items: center;
  height: 33px;
  box-sizing: border-box;
  border: solid 1px ${(props) => props.theme.colors.buttonBorderNew};
  border-radius: 2px;
  justify-content: space-between;
  align-self: center;
  color: ${(props) => props.theme.colors.onBackground};
  padding: 15px 20px;
  ${validFor.desktop`
    padding: 8px 12px;
  `};
`

const StyledTiny2 = styled(Tiny2)`
  margin: 0;
  font-weight: 700;
  letter-spacing: -1%;
  line-height: 1;
  font-size: 14px;
  ${hideTextMediaQueries}
`

const StyledTiny2Medium = styled(StyledTiny2)`
  font-weight: 400;
  font-size: 14px;
  ${hideTextMediaQueries}
`

const StyledBadgeRedesign = styled(BadgeRedesign)`
  min-width: 16px;
  height: 16px;

  p {
    font-size: 10px;
  }
`
